import { useMutation, useQuery } from "@tanstack/react-query";
import { notification } from "antd";

import { APP_PERMISSION_TYPES, QUERY_NAME, SERVICES } from "@lhd/constants";
import axios from "@lhd/libs/axios";
import { App, Orgs, Permission, Position, Worker } from "@lhd/types";
import { getAccessToken, getCompanyId } from "@lhd/utility";
import { AppRecordPermission } from "@lhd/features/Apps/AppSetting/AppPermission/RowAppRecordPermissions/hook";
import { AppRecordPermissionBody } from "types/appPermission.type";
import { toNumber } from "lodash";
import axiosInstanceV2 from "@lhd/libs/axiosV2";
import {
  mergeOrgIdsIntoOrgNodeIds,
  normalizeSavePayloadRecursively,
} from "@lhd/utility";

export const useFetchAppPermissions = (
  page = 1,
  pageSize = 1000,
  target?: any,
  params?: Record<string, string | number | boolean>,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.APP_PERMISSIONS, companyId, page, pageSize],
    () =>
      axios({
        method: "get",
        url: SERVICES.APP_PERMISSIONS,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          ...(target && {
            target: target,
          }),
          ...params,
        },
      }).then((res) => ({
        data: res.data as Permission[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 100,
      staleTime: 100,
    },
  );
};

export const useFetchAppsWithPermission = (
  page: number,
  pageSize: number,
  filterParams?: any,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.APPS_WITH_PERMISSIONS, companyId, page, pageSize, filterParams],
    () =>
      axios({
        method: "get",
        url: SERVICES.APPS,
        params: {
          page,
          per_page: pageSize,
          ...filterParams,
          "q[company_id_eq]": companyId,
          current_login_company_id: companyId,
          is_flatten: true,
          is_app_permissions: true,
          expose_attributes: [
            "app_permissions",
            "id",
            "name",
            "created_by",
            "created_at",
            "updated_at",
          ],
        },
      }).then((res) => ({
        data: res.data as App[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
  );
};

export const useFetchPositionInAppPermissions = (
  orgNodeIds?: string,
  workerIds?: string,
  name?: string,
  orgIds?: string,
) => {
  const companyId = getCompanyId();
  const mergedOrgNodeIds = mergeOrgIdsIntoOrgNodeIds(orgIds, orgNodeIds);
  return useQuery(
    [
      QUERY_NAME.APP_PERMISSIONS_LIST_POSITIONS,
      companyId,
      orgNodeIds,
      workerIds,
      name,
      orgIds,
    ],
    () =>
      axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANY_WORKER_PERMISSIONS}/list-positions`,
        params: {
          company_id: companyId,
          ...(mergedOrgNodeIds && { org_node_ids: mergedOrgNodeIds }),
          ...(workerIds && { worker_ids: workerIds }),
          ...(name && { name }),
          ...(orgIds && { org_ids: orgIds }),
        },
      }).then((res) => ({
        data: res.data as Position[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 300,
      staleTime: 300,
    },
  );
};

export const useFetchWorkersInAppPermissions = () => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.APP_PERMISSIONS_LIST_WORKERS, companyId],
    () =>
      axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANY_WORKER_PERMISSIONS}/list-workers`,
        params: {
          company_id: companyId,
          page: 1,
          per_page: 1000000,
        },
      }).then((res) => ({
        data: res.data as Worker[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 300,
      staleTime: 300,
    },
  );
};

export const useFetchOrgsInAppPermissions = (
  name?: string,
  positionIds?: string,
  workerIds?: string,
  options?: any,
  skipSpecificPage?: string,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [
      QUERY_NAME.APP_PERMISSIONS_LIST_ORGS,
      companyId,
      name,
      positionIds,
      workerIds,
      skipSpecificPage,
    ],
    () =>
      axiosInstanceV2({
        method: "get",
        url: `${SERVICES.COMPANY_WORKER_PERMISSIONS}/list-orgs-and-nodes-full`,
        params: {
          company_id: companyId,
          ...(name ? { name } : {}),
          ...(positionIds && { position_ids: positionIds }),
          ...(workerIds && { worker_ids: workerIds }),
          ...(skipSpecificPage && { skip_specific_page: skipSpecificPage }),
        },
      }).then((res) => ({
        data: res.data as Orgs[],
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 300000,
      staleTime: 300000,
      ...options,
    },
  );
};

export const useFetchAllAppPermissionCurrentUser = (enabledOverride = true) => {
  const companyId = getCompanyId();
  const accessToken = getAccessToken();
  return useQuery(
    [QUERY_NAME.APP_PERMISSIONS_CURRENT_USER, companyId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APP_PERMISSIONS}/for-user-all-apps`,
        params: {
          current_login_company_id: companyId,
        },
      }).then((res) => res.data),
    {
      cacheTime: 5000,
      staleTime: 5000,
      enabled: enabledOverride && !!companyId && !!accessToken,
    },
  );
};

export const useSubmitAppPermission = () => {
  const companyId = getCompanyId();

  return useMutation(
    ({ payload }: any) =>
      axios({
        method: "post",
        url: `${SERVICES.APP_PERMISSIONS}/save`,
        data: {
          ...normalizeSavePayloadRecursively(payload),
          company_id: companyId,
        },
      }),
    {
      onSuccess() {
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};

export const useCheckAppPermission = (
  arrPermission: (keyof typeof APP_PERMISSION_TYPES)[],
) => {
  const { data: dataAppPermissionCurrentUser } =
    useFetchAllAppPermissionCurrentUser();
  // const auth = useSelector(user);

  // convert array permission to boolean => ex: ["add_new_app", "general_app_read"] => [true, false] => if have false, it's invalid
  const checkAppPermission = arrPermission.map(
    (permisisonItem) => !!dataAppPermissionCurrentUser?.[permisisonItem],
  );

  const isValid = !checkAppPermission.includes(false);
  return {
    isValid: isValid,
  };
};

export const useFetchAppraisalPermission = (enabledOverride = true) => {
  const companyId = getCompanyId();
  const accessToken = getAccessToken();
  return useQuery(
    [QUERY_NAME.APP_PERMISSIONS_APPRAISAL, companyId],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APP_PERMISSIONS}/appraisal-user`,
        params: {
          company_id: companyId,
        },
      }).then((res) => res.data),
    {
      cacheTime: 5000,
      staleTime: 5000,
      enabled: enabledOverride && !!companyId && !!accessToken,
    },
  );
};
//for app record permission

export const useFetchAppRecordPermissions = (
  page: number,
  pageSize: number,
  appId: number,
  //filterParams?: any,
) => {
  const companyId = getCompanyId();
  return useQuery(
    [QUERY_NAME.APP_RECORD_PERMISSIONS, companyId, page, pageSize],
    () =>
      axios({
        method: "get",
        url: `${SERVICES.APP_PERMISSIONS}/records-screen`,
        params: {
          page,
          per_page: pageSize,
          company_id: companyId,
          app_id: appId,
        },
      }).then((res) => ({
        data: res.data as AppRecordPermission,
        meta: {
          total: res.headers["x-total"] || 0,
          total_page: res.headers["x-total-pages"] || 0,
        },
      })),
    {
      cacheTime: 100,
      staleTime: 100,
    },
  );
};

export const useSavePermissionRecord = () => {
  const companyId = getCompanyId();
  return useMutation(
    (payload: AppRecordPermissionBody) =>
      axios({
        method: "post",
        url: `${SERVICES.APP_PERMISSIONS}/save`,
        data: {
          ...normalizeSavePayloadRecursively(payload),
          company_id: toNumber(companyId),
        },
      }),
    {
      onSuccess() {
        notification["success"]({
          message: "保存しました",
        });
      },
    },
  );
};
